// When subtitles and subtitle-free annotations show.
//
// A subtitle or annotation has a start time and, optionally, an end time.
// Without an end it lasts until the next "moment" starts: the next subtitle
// when the video has subtitles (an annotation belongs to the subtitle it is
// said in), else the next annotation. So only one subtitle, with its
// annotations, shows at a time.

// An end time counts only if it is after the start: an end at (or before)
// the start would show the item for no time at all, so it is read as no end
// (until the next one).
export const hasEnd = (item) => typeof item.endTime === 'number' && !Number.isNaN(item.endTime)
  && item.endTime > item.startTime;

/** The start of the next moment after time t, or null if there is none. */
export function nextStartAfter(t, subtitles = [], annotations = []) {
  const pool = subtitles.length > 0 ? subtitles : annotations;
  let next = null;
  pool.forEach((x) => {
    if (x.startTime > t && (next === null || x.startTime < next)) next = x.startTime;
  });
  return next;
}

/** When the item stops showing: its end time, else the next moment's start (Infinity if none). */
export function effectiveEnd(item, subtitles = [], annotations = []) {
  if (hasEnd(item)) return item.endTime;
  const next = nextStartAfter(item.startTime, subtitles, annotations);
  return next === null ? Infinity : next;
}

/** Whether the item shows at time t. An explicit end is inclusive; an open end stops as the next moment starts. */
export function isShowing(item, t, subtitles = [], annotations = []) {
  if (t < item.startTime) return false;
  if (hasEnd(item)) return t <= item.endTime;
  return t < effectiveEnd(item, subtitles, annotations);
}

/** The index of the subtitle showing at t: the latest-starting one, so overlaps show one at a time. -1 if none. */
export function currentSubtitleIndexAt(t, subtitles = []) {
  let found = -1;
  subtitles.forEach((s, i) => {
    if (isShowing(s, t, subtitles) && (found === -1 || s.startTime >= subtitles[found].startTime)) found = i;
  });
  return found;
}

// How soon after an annotation a subtitle may start and still be the one it
// belongs to (a word noted just before its line was timed).
export const ALIGN_LOOKAHEAD = 2;

// A subtitle without an end shows until the next one, however far; for
// aligning new words it only claims this long after its start, so words
// noted for a subtitle not added yet don't attach to an old one.
export const OPEN_ALIGN_SPAN = 10;

/**
 * The subtitle an annotation starting at t belongs to: the one showing at t
 * (an open-ended one only within OPEN_ALIGN_SPAN of its start), else one
 * starting within ALIGN_LOOKAHEAD seconds after it. Null if none.
 */
export function subtitleFor(t, subtitles = []) {
  const idx = currentSubtitleIndexAt(t, subtitles);
  if (idx !== -1) {
    const s = subtitles[idx];
    if (hasEnd(s) || t - s.startTime <= OPEN_ALIGN_SPAN) return s;
  }
  let best = null;
  subtitles.forEach((s) => {
    if (s.startTime > t && s.startTime <= t + ALIGN_LOOKAHEAD && (!best || s.startTime < best.startTime)) best = s;
  });
  return best;
}

export const endOf = (item) => (hasEnd(item) ? item.endTime : null);

/** Whether an annotation has the same start and end as the subtitle. */
export const isAlignedWith = (annotation, subtitle) =>
  annotation.startTime === subtitle.startTime && endOf(annotation) === endOf(subtitle);

/**
 * Annotations showing at t that no subtitle covers yet (annotated before
 * their subtitle was added): a subtitle added here aligns with them.
 */
export function looseAnnotationsAt(t, subtitles = [], annotations = []) {
  return annotations.filter((a) => isShowing(a, t, subtitles, annotations) && !subtitleFor(a.startTime, subtitles));
}

/**
 * The window a subtitle added at t takes from the loose annotations there:
 * their earliest start, and their end if they all share one (else open).
 * Null when there are none.
 */
export function windowOfAnnotations(anns) {
  if (anns.length === 0) return null;
  const start = Math.min(...anns.map((a) => a.startTime));
  const ends = new Set(anns.map(endOf));
  return { start, end: ends.size === 1 ? [...ends][0] : null };
}

const byStart = (a, b) => a.startTime - b.startTime;

// The place the teacher gave a word among its subtitle's words (its
// `order`, Requirement 58); words never placed come after, by time.
const orderKey = (a) => (typeof a.order === 'number' ? a.order : Infinity);
/** A subtitle's words in the teacher's order, then by start time. */
export const byOrder = (a, b) => (orderKey(a) - orderKey(b)) || ((a.startTime || 0) - (b.startTime || 0));
/** The title's words in the teacher's order, then alphabetically. */
export const byTitleOrder = (a, b) => (orderKey(a) - orderKey(b))
  || (a.surfaceText || '').localeCompare(b.surfaceText || '');

// How far into a subtitle "previous" still means the one before it, not
// the start of this one (as on a music player).
export const PREV_GRACE = 1;

/**
 * Where ‹ / › jump to from time t: the start of the previous / next subtitle
 * or annotation (null if none). ‹ within PREV_GRACE s of a start goes to the
 * one before.
 */
export function stepTargets(t, subtitles = [], annotations = []) {
  const starts = [...new Set([...subtitles, ...annotations]
    .map((x) => x.startTime).filter((s) => typeof s === 'number'))].sort((a, b) => a - b);
  const before = starts.filter((s) => s < t - PREV_GRACE);
  return {
    prev: before.length ? before[before.length - 1] : null,
    next: starts.find((s) => s > t + 0.05) ?? null,
  };
}

/**
 * The subtitles around an annotation starting at t: the one it belongs to
 * (subtitleFor), and the ones before and after that one, or around t when it
 * belongs to none. For moving a word to the previous or next subtitle.
 */
export function subtitlesAround(t, subtitles = []) {
  const sorted = [...subtitles].sort(byStart);
  const own = subtitleFor(t, sorted);
  if (own) {
    const i = sorted.indexOf(own);
    return { own, prev: sorted[i - 1] || null, next: sorted[i + 1] || null };
  }
  const before = sorted.filter((s) => s.startTime < t);
  return { own: null, prev: before[before.length - 1] || null, next: sorted.find((s) => s.startTime > t) || null };
}

/**
 * The video as bunches, by start time: each subtitle with the annotations
 * that belong to it, and annotations that belong to no subtitle, bunched by
 * their start. [{ key, subtitle | null, startTime, annotations }]
 */
export function bunches(subtitles = [], annotations = []) {
  const sorted = [...subtitles].sort(byStart);
  const bySub = new Map(sorted.map((s) => [s.id, { key: `s:${s.id}`, subtitle: s, startTime: s.startTime, annotations: [] }]));
  const loose = new Map();
  [...annotations].sort(byStart).forEach((a) => {
    const own = subtitleFor(a.startTime, sorted);
    if (own) {
      bySub.get(own.id).annotations.push(a);
      return;
    }
    const key = `t:${a.startTime}`;
    if (!loose.has(key)) loose.set(key, { key, subtitle: null, startTime: a.startTime, annotations: [] });
    loose.get(key).annotations.push(a);
  });
  const all = [...bySub.values(), ...loose.values()];
  all.forEach((b) => b.annotations.sort(byOrder));
  return all.sort((a, b) => a.startTime - b.startTime);
}

// Times this close count as the same moment (they are kept to tenths).
const TOUCH = 0.05;

/**
 * When a subtitle's end is changed, the next subtitle's start follows it if
 * it started where this one ended (they touched), or if the new end would
 * run into it (Requirement 59). Null when the next subtitle stays as it is;
 * else { next, startTime } and `blocked` when the next one would start at or
 * after its own end.
 */
export function followingStart(subtitle, changes, subtitles = []) {
  const newEnd = changes.endTime;
  if (typeof newEnd !== 'number') return null;
  const next = subtitles
    .filter((s) => s.id !== subtitle.id && s.title !== true && s.startTime >= subtitle.startTime)
    .sort(byStart)[0];
  if (!next) return null;
  const oldEnd = hasEnd(subtitle) ? subtitle.endTime : null;
  const touched = oldEnd !== null && Math.abs(next.startTime - oldEnd) < TOUCH;
  if (!touched && newEnd <= next.startTime) return null;
  if (Math.abs(newEnd - next.startTime) < TOUCH) return null;
  return { next, startTime: newEnd, blocked: hasEnd(next) && newEnd >= next.endTime };
}
